
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  processAdditionalContacts, 
  processPayrollSchedules, 
  ensureStringArray,
  normalizeCorporateStructure,
  processHealthPlans
} from '@/utils/application-data-utils';
import type { Application, HealthPlan } from '@/types/database.types';
import type { Json } from '@/integrations/supabase/types';

/**
 * Hook for fetching multiple applications for a user
 */
export const useFetchApplications = () => {
  const [isLoading, setIsLoading] = useState(false);
  
  const fetchApplications = async (userId: string): Promise<Application[]> => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('applications')
        .select('*, profiles(email, first_name, last_name)')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching applications:', error);
        throw error;
      }
      
      if (!data || data.length === 0) return [];
      
      // Process the raw data to match the Application type
      const processedData = data.map(item => ({
        ...item,
        // Map joined profile data to application fields for ApplicationWithUser interface
        user_email: (item as any).profiles?.email || item.email,
        user_first_name: (item as any).profiles?.first_name || item.first_name,
        user_last_name: (item as any).profiles?.last_name || item.last_name,
        // Convert JSON fields to proper typed arrays
        additional_contacts: processAdditionalContacts(item.additional_contacts as Json),
        additional_schedules: processPayrollSchedules(item.additional_schedules as Json[]),
        // Process health plans properly
        health_plans: processHealthPlans(item.health_plans as Json),
        // Ensure string arrays - convert DB string values to arrays
        desired_medical_carriers: ensureStringArray(item.desired_medical_carriers),
        desired_ancillary_carriers: ensureStringArray(item.desired_ancillary_carriers),
        other_documents: ensureStringArray(item.other_documents),
        corporate_structure: normalizeCorporateStructure(item.corporate_structure),
        // Process additional array fields that might be strings in the database
        states_with_employees: ensureStringArray(item.states_with_employees),
        top_3_peo_needs: ensureStringArray(item.top_3_peo_needs),
        preferred_peos: ensureStringArray(item.preferred_peos),
        excluded_peos: ensureStringArray(item.excluded_peos),
        states_of_operation: ensureStringArray(item.states_of_operation)
      })) as unknown as Application[];
      
      return processedData;
    } catch (error) {
      console.error('Error fetching applications:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    fetchApplications,
    isLoading,
  };
};
